/* Copyright (c) 2020-present CloudIO Inc */

import {
  Attribute,
  IoDatasourceAttributes,
  getFilterDataType,
} from '@cloudio-saas/datasource-types';
import { emptyFunction } from '@cloudio-saas/statex';
import { AnyItem, ItemType, Page } from '../../registry/ComponentRegistry';
import {
  AttributeBaseDataType,
  AttributeDataType,
  toAttributeDataType,
} from 'componentregistry/components/SmartSearch';
import { isFixedOperator } from 'components/SmartSearch/SearchUtils';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  RequiredItem,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { returnType } from '../ActionSet/ActionSetManager';
import { defaultValue } from './ActionSetQueryFilter';
import { isNotEmpty } from '../../utils/isEmpty';

export async function getDataType({
  appUid,
  pageId,
  itemId,
  attribute,
  platform,
  itemType,
  storeType,
}: {
  appUid: string;
  pageId: string;
  itemId: string;
  attribute: string;
  platform: Platform;
  itemType: ItemType;
  storeType: 'QUERY_FILTER' | 'STORE';
}): Promise<[AttributeDataType, Attribute | null, AttributeBaseDataType]> {
  if (attribute) {
    const targetId =
      itemType === 'SmartSearchAttribute'
        ? platform.findParentOfType(appUid, pageId, itemId, 'SmartSearch')
            ?.props.gridId ||
          platform.findParentOfType(appUid, pageId, itemId, 'SmartSearch')
            ?.props.pivotId ||
          itemId
        : itemId;
    const aliasAndEmbeddedPageIdValue = platform.getDataSourceAlias(
      appUid,
      pageId,
      targetId,
      storeType,
    );

    if (!aliasAndEmbeddedPageIdValue) {
      return ['Text', null, 'Text'];
    }

    const store = isNotEmpty(aliasAndEmbeddedPageIdValue.alias)
      ? platform.getStore(appUid, pageId, aliasAndEmbeddedPageIdValue.alias)
      : undefined;

    if (!store) {
      platform.showError(
        `Missing parent store for ${itemType} with itemId: ${itemId} in page ${pageId}!`,
        { appUid, pageId, itemId },
      );
      return ['Text', null, 'Text'];
    }
    await store.initializeAttributes();
    const attr = store.getAttribute(attribute);
    if (!attr) {
      platform.showError(
        `Invalid attribute '${attribute}' specified in ${itemType} with itemId: ${itemId}!`,
        { appUid, pageId, itemId },
      );
      return ['Text', null, 'Text'];
    }
    const normalizedAttributeType = getFilterDataType(attr);
    const baseDataType = getFilterBaseDataType(attr);
    return [toAttributeDataType(normalizedAttributeType), attr, baseDataType];
  }
  return ['Text', null, 'Text'];
}
export const getFilterBaseDataType: (arg0: IoDatasourceAttributes) => any = (
  a: IoDatasourceAttributes,
) => {
  if (a.attributeType === 'DateTime') {
    return 'DateTime';
  } else if (a.attributeType === 'StringDateTime') {
    return 'StringDateTime';
  }
  const normalizedAttributeType = getFilterDataType(a);
  return toAttributeDataType(normalizedAttributeType);
};

const ActionSetQueryFilterManager: ActionManagerFn<'ActionSetQueryFilter'> = (
  platform: Platform,
): ActionManager<'ActionSetQueryFilter'> => {
  const dataType = 'Date';
  let callback = emptyFunction;
  let operatorCallback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionSetQueryFilter'),
    defaultValue,
    props: {
      ...baseActionProps,
      attribute: {
        label: 'Attribute',
        type: 'ViewAttributeChooser',
        required: true,
        aliasType: 'QUERY_FILTER',
        getAttributeDataTypes: () => 'ALL',
        preventMultiEdit: true,
      },
      operator: {
        label: 'Filter Operator',
        type: 'operator',
        required: true,
        refreshCallback: (cb: () => void) => {
          operatorCallback = cb;
        },
        getDataType: async (
          item: RequiredItem<'ActionSetQueryFilter'>,
          { appUid, pageId, itemId },
        ) => {
          const [attrDataType] = await getDataType({
            appUid,
            pageId,
            itemId,
            attribute: item.props.attribute,
            itemType: item.type,
            platform,
            storeType: 'QUERY_FILTER',
          });
          return attrDataType;
        },
      },
      hasTime: {
        label: 'Has Time',
        type: 'boolean',
      },
      value: {
        label: 'Value',
        type: 'expressionArray',
        required: true,
        refreshCallback: (cb: () => void) => {
          callback = cb;
        },
        returnType: async (
          item: RequiredItem<'ActionSetQueryFilter'>,
          { appUid, pageId, itemId },
        ) => {
          return returnType({
            appUid,
            pageId,
            itemId,
            attribute: item.props.attribute,
            itemType: item.type,
            platform,
            storeType: 'QUERY_FILTER',
          });
        },
      },
    },
    onPropertyChange: (property, value) => {
      if (property === 'attribute') {
        setTimeout(() => {
          callback();
          operatorCallback();
        }, 100);
        return {
          [property]: value,
          operator: defaultValue.operator,
          value: defaultValue.value,
        };
      }
      return {
        [property]: value,
      };
    },
    isAllowedInsideParent: (page: Page, parent: AnyItem) => {
      return (
        [
          'ActionQuery',
          'ActionQueryChart',
          'ActionQuerySelect',
          'ActionSetDefaultFilters',
          'ActionSetFilterCombiner',
          'TriggerBeforeQuery',
          'ActionQueryCalendar',
        ] as ItemType[]
      ).includes(parent.type);
    },
    isPropertyVisible(prop, itemData): boolean {
      const { attribute, operator } = itemData;
      switch (prop) {
        case 'operator':
          return !!attribute;
        case 'hasTime':
          return !!attribute && dataType === 'Date';
        case 'value':
          return !!attribute && !isFixedOperator(operator);
        default:
          return true;
      }
    },
    getDisplayText: (item) =>
      item.props.operator
        ? `${item.props.attribute} [${item.props.operator}]`
        : item.props.attribute,
    name: 'Set Query Filter',
    description: `**Overview**<br/>
The Set Query Filter action is a comprehensive data filtering component designed to apply specific, operator-based constraints to a data retrieval operation. Unlike the "Set Simple Query Filter" which implies strict equality, this component allows developers to define complex relationships between data fields and values using a wide range of logical operators (such as "Contains," "Greater Than," "Is Not Null," or "Starts With").<br/><br/>
This component cannot exist independently; it is strictly an architectural element used within parent query actions (like ActionQuery, ActionQueryChart, or ActionSetDefaultFilters). It serves as the primary tool for constructing sophisticated search queries, reporting filters, and data segmentation logic.

**Key Features**<br/>
* **Advanced Operator Support:** Provides a full suite of comparison operators adaptable to the attribute type. For example, text fields offer "Contains" or "Starts With," while number fields offer "Greater Than" or "Less Than."
* **Smart UI Adaptation:** The configuration interface is context-aware. If an operator like "Is Null" or "Is Empty" is selected, the "Value" field is automatically hidden since no input is required.
* **Type-Safe Validation:** Uses getDataType to dynamically analyze the selected attribute's schema definition, ensuring that the value expression provided matches the expected format (e.g., enforcing Date objects for Date fields).
* **Strict Parent Scope:** Enforces usage validity via isAllowedInsideParent, ensuring it only functions within valid data querying contexts or filter combiners.

**Properties**<br/>
* **Attribute (Required):** A ViewAttributeChooser property.
  * *Function:* Selects the specific field (column) in the database to filter against.
  * *Behavior:* Changing this property resets the Operator and Value fields to prevent type mismatches.
* **Filter Operator (Required):** A selection property.
  * *Function:* Defines the logical relationship (e.g., Equals, NotEquals, Contains, GreaterThan, In).
  * *Options:* Dynamically populated based on the selected Attribute's data type.
* **Value (Required):** An expression property (Expression Array).
  * *Function:* The value(s) to compare against. Supports dynamic variables and calculations.
  * *Visibility:* Hidden for unary operators like IsNull or IsNotNull.
* **Has Time:** A boolean property.
  * *Function:* Specific to Date attributes; specifies if the time component of the date should be considered in the filter logic.

**Use Cases**<br/>
* **Search Functionality:** Implementing a "Search by Name" feature using the Contains operator so that typing "Smith" finds "John Smith" and "Smithsonian."
* **Status Exclusion:** Configuring a default filter to load a task list where Status NotEquals "Archived."
* **Value Ranges:** Filtering a product list to show items where Price is GreaterThan 50.
* **Data Auditing:** Finding records where a mandatory field like AssignedManager IsNull to identify gaps in data entry.`,

    icon: { iconName: 'filter' },
  };
};

export default ActionSetQueryFilterManager;
